import React, { useEffect, useState } from "react";
import {
  Play,
  Square,
  Loader2,
  Download,
  Activity,
  AlarmClockOff,
  RadioTower,
  X,
} from "lucide-react";
import { batteryDisplay } from "utils/wearableStatus";
import { useTranslation } from "react-i18next";
import { dateLocale } from "i18n";

const FOCUS =
  "focus:outline-none focus-visible:ring-2 focus-visible:ring-[#E07A3A]/45 focus-visible:ring-offset-1 focus-visible:ring-offset-white";

const timeOf = (ts) =>
  new Date(ts).toLocaleTimeString(dateLocale("en-GB"), { hour12: false });

const fmtElapsed = (ms) => {
  const s = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  return h > 0 ? `${h}h ${m}m` : `${m}:${String(sec).padStart(2, "0")}`;
};

// "2m 26s" / "46s" — countdowns read as time, not a raw second count.
const fmtCountdown = (s) => {
  const secs = Math.max(0, Math.round(s));
  if (secs < 90) return `${secs}s`;
  return `${Math.floor(secs / 60)}m ${secs % 60}s`;
};

const fmtDuration = (ms) => {
  const s = Math.max(0, Math.round(ms / 1000));
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  return `${m}m ${s % 60}s`;
};

/**
 * One reading in the hero's strip. Each fact lives here and nowhere else on
 * the screen, and the action that changes it (Clear, Update) sits beside it
 * rather than in a separate toolbar.
 */
const Cell = ({ label, children, hint, action }) => (
  <div className="min-w-0 px-5 py-4" title={hint}>
    <div className="flex h-6 items-center justify-between gap-2">
      <span className="text-[11px] font-semibold uppercase tracking-[0.08em] text-[#374151]">
        {label}
      </span>
      {action}
    </div>
    <div className="mt-1 flex min-w-0 items-baseline gap-1.5 truncate text-[17px] font-semibold tabular-nums text-[#111827]">
      {children}
    </div>
  </div>
);

const Suffix = ({ children, className = "text-[#9CA3AF]" }) => (
  <span className={`text-[12px] font-medium ${className}`}>{children}</span>
);

const CellAction = ({ onClick, disabled, title, danger, children }) => (
  <button
    onClick={onClick}
    disabled={disabled}
    title={title}
    className={`rounded-md border px-2 py-0.5 text-[12px] font-medium transition duration-150 disabled:pointer-events-none disabled:opacity-40 ${FOCUS} ${
      danger
        ? "border-[#FECACA] text-[#DC2626] hover:bg-[#FEF2F2]"
        : "border-[#F3D3BE] text-[#E07A3A] hover:bg-[#FFF3ED]"
    }`}
  >
    {children}
  </button>
);

/**
 * The hero card: what the device is doing right now, its one obvious next
 * action, and the four readings that matter. The activity feed, sessions and
 * manufacturing live in the tabs beneath it. All stream-derived state comes
 * in as props from the modal's useLiveFeed hook, so this view can unmount
 * (flows) without losing or replaying history.
 */
const LiveDeviceView = ({
  connected,
  devCfg,
  conn, // resolved state chip {label,color,bg,live} from the workbench
  waking,
  commandsLocked,
  anyBusy,
  flowActive,
  showStartSession,
  sessionBusy,
  onStartSession,
  onGoIdle,
  onExport,
  exportBusy,
  linesReceived,
  exportTotal,
  onEraseAll,
  storeEmpty,
  onUpdateFirmware,
  onSyncTime,
  // from useLiveFeed
  pulse,
  sessionStart,
  sessionSummary,
  armingInfo,
  eraseProgress,
  onDismissSummary,
}) => {
  const { t } = useTranslation();
  const [, forceTick] = useState(0);

  const inSession = devCfg?.state === "SESSION";

  // A once-a-second re-render, only while the elapsed readout is visible.
  useEffect(() => {
    if (!inSession || !sessionStart) return undefined;
    const id = setInterval(() => forceTick((n) => n + 1), 1000);
    return () => clearInterval(id);
  }, [inSession, sessionStart]);

  const controlsLocked = waking || commandsLocked || anyBusy;
  const toolsLocked = !connected || controlsLocked || flowActive;
  const devState = String(devCfg?.state || "").toUpperCase();
  const primary = inSession
    ? {
        id: "end",
        label: t("devices.liveView.endSession"),
        icon: Square,
        action: onGoIdle,
      }
    : devState && devState !== "IDLE" && devState !== "STANDBY"
    ? {
        id: "idle",
        label: t("devices.liveView.returnToIdle"),
        icon: Square,
        action: onGoIdle,
      }
    : showStartSession
    ? {
        id: "start",
        label: t("devices.liveView.startSession"),
        icon: Play,
        action: onStartSession,
      }
    : null;

  const pulseMax = pulse.length ? Math.max(...pulse) : 0;
  const threshold = devCfg?.impactG ?? null;

  // Session summary extras computed at render time: the record count settles
  // a few seconds after the session ends (next status poll).
  const summaryNewRecords =
    sessionSummary?.startRecords != null && devCfg?.records != null
      ? Math.max(0, devCfg.records - sessionSummary.startRecords)
      : null;

  const batt = batteryDisplay(devCfg);

  return (
    <div>
      <section className="overflow-hidden rounded-2xl border border-[#EDEDEA] bg-white">
        <div className="flex flex-wrap items-center justify-between gap-x-6 gap-y-4 px-6 pb-6 pt-7">
          <div className="min-w-0">
            <div className="flex items-center gap-3">
              <span className="relative flex h-3 w-3 flex-shrink-0">
                {conn.live && (
                  <span
                    className="absolute inline-flex h-full w-full animate-ping rounded-full opacity-50"
                    style={{ backgroundColor: conn.color }}
                  />
                )}
                <span
                  className="relative inline-flex h-3 w-3 rounded-full"
                  style={{ backgroundColor: conn.color }}
                />
              </span>
              <h2 className="truncate text-[28px] font-bold leading-tight tracking-tight text-[#111827]">
                {conn.label}
              </h2>
              {inSession && devCfg?.sessionId != null && (
                <span className="rounded bg-[#F5F4F1] px-1.5 py-0.5 font-mono text-[11px] font-medium tabular-nums text-[#6B7280]">
                  {t("devices.liveView.sessionId", { id: devCfg.sessionId })}
                </span>
              )}
            </div>
            <p className="mt-1 text-[14px] text-[#6B7280]">
              {inSession
                ? sessionStart
                  ? t("devices.liveView.startedElapsed", {
                      time: timeOf(sessionStart),
                      elapsed: fmtElapsed(Date.now() - sessionStart),
                    })
                  : t("devices.liveView.inProgressBefore")
                : devCfg?.storeFull
                ? t("devices.liveView.storeFull")
                : devCfg?.records > 0
                ? t("devices.liveView.readyRecords", {
                    count: devCfg.records,
                  })
                : devCfg?.records != null
                ? t("devices.liveView.readyEmpty")
                : t("devices.liveView.waitingForReport")}
            </p>
            {/* Arming is a phase machine, not a single wait — narrate it.
                A bare "Arming" for minutes (grace, then a BLE link holding
                the start open) reads as a hang. */}
            {inSession && devCfg?.sub === "arming" && (
              <p
                className={`mt-1 text-[13px] font-medium ${
                  armingInfo?.phase === "hold"
                    ? "text-[#B45309]"
                    : "text-[#6B7280]"
                }`}
              >
                {(() => {
                  if (!armingInfo)
                    return t("devices.liveView.arming.donningGrace");
                  const left =
                    armingInfo.secondsLeft != null
                      ? Math.max(
                          0,
                          Math.round(
                            armingInfo.secondsLeft -
                              (Date.now() - armingInfo.at) / 1000
                          )
                        )
                      : null;
                  // Countdown + the actual clock time it lands, so nobody has
                  // to do arithmetic on "~146s".
                  const eta =
                    left != null && left > 0
                      ? t("devices.liveView.arming.eta", {
                          time: timeOf(Date.now() + left * 1000),
                        })
                      : "";
                  if (armingInfo.phase === "hold") {
                    const reason = armingInfo.assigned
                      ? t("devices.liveView.arming.holdReasonHub")
                      : t("devices.liveView.arming.holdReasonConnected");
                    if (left == null)
                      return t("devices.liveView.arming.holdNoCountdown", {
                        reason,
                      });
                    return left > 0
                      ? t("devices.liveView.arming.holdStartsIn", {
                          reason,
                          countdown: fmtCountdown(left),
                          eta,
                        })
                      : t("devices.liveView.arming.holdStartingNow", {
                          reason,
                        });
                  }
                  if (armingInfo.phase === "sched") {
                    return t("devices.liveView.arming.schedWait", {
                      startsIn: armingInfo.startsIn
                        ? t("devices.liveView.arming.schedIn", {
                            startsIn: armingInfo.startsIn,
                          })
                        : "",
                      startsAt: armingInfo.startsAt
                        ? t("devices.liveView.arming.schedAt", {
                            startsAt: armingInfo.startsAt,
                          })
                        : "",
                    });
                  }
                  if (left == null)
                    return t("devices.liveView.arming.donningGrace");
                  if (left <= 0)
                    return t("devices.liveView.arming.countdownElapsed");
                  // A countdown longer than the 60 s grace is a hub-scheduled
                  // start, not donning grace — don't mislabel it.
                  return t(
                    left > 70
                      ? "devices.liveView.arming.scheduledStartIn"
                      : "devices.liveView.arming.donningGraceIn",
                    { countdown: fmtCountdown(left), eta }
                  );
                })()}
              </p>
            )}
            {/* Store erase in progress (v0.5.86 reports every erase path). */}
            {eraseProgress && (
              <div className="mt-1.5 flex items-center gap-2">
                <span className="text-[13px] font-medium text-[#B45309]">
                  {t("devices.liveView.erasingStore")}
                  {eraseProgress.records != null
                    ? t("devices.liveView.eraseRecords", {
                        count: eraseProgress.records,
                      })
                    : ""}
                  {eraseProgress.pct != null ? ` · ${eraseProgress.pct}%` : ""}
                  {eraseProgress.done != null && eraseProgress.total != null
                    ? t("devices.liveView.eraseSectors", {
                        done: eraseProgress.done,
                        total: eraseProgress.total,
                      })
                    : ""}
                </span>
                <span className="h-1.5 w-32 overflow-hidden rounded-full bg-[#F5E9DD]">
                  <span
                    className="block h-full rounded-full bg-[#E07A3A] transition-all duration-300"
                    style={{ width: `${eraseProgress.pct ?? 4}%` }}
                  />
                </span>
              </div>
            )}
            {/* Conditions that silently ruin a session get a line here, and
                only when they are true. */}
            {devCfg?.castTx === false && (
              <p className="mt-1.5 flex items-center gap-1.5 text-[13px] font-medium text-[#B45309]">
                <RadioTower size={13} />
                {t("devices.workbench.silentTitle")}
              </p>
            )}
            {devCfg?.timeSet === false && (
              <p className="mt-1.5 flex items-center gap-1.5 text-[13px] font-medium text-[#B45309]">
                <AlarmClockOff size={13} />
                {t("devices.workbench.clockNotSynced")}
                <button
                  onClick={() => onSyncTime?.()}
                  disabled={commandsLocked}
                  className={`rounded bg-[#E07A3A] px-1.5 py-0.5 text-[11px] font-semibold text-white transition duration-150 hover:bg-[#D66B2E] disabled:opacity-50 ${FOCUS}`}
                >
                  {t("devices.workbench.sync")}
                </button>
              </p>
            )}
          </div>

          <div className="flex flex-shrink-0 flex-wrap items-center gap-2">
            <button
              onClick={onExport}
              disabled={controlsLocked || inSession || flowActive}
              title={
                inSession
                  ? t("devices.liveView.exportEndSessionFirst")
                  : t("devices.summaryCard.exportTitle")
              }
              className={`flex items-center gap-2 rounded-[10px] border border-[#E5E7EB] bg-white px-4 py-2.5 text-sm font-medium text-[#374151] transition duration-150 hover:bg-[#F9FAFB] disabled:opacity-50 ${FOCUS}`}
            >
              {exportBusy ? (
                <>
                  <Loader2 size={15} className="animate-spin" />
                  {linesReceived > 0
                    ? exportTotal > 0
                      ? `${Math.min(
                          100,
                          Math.round((linesReceived / exportTotal) * 100)
                        )}%…`
                      : t("devices.liveView.exportRecordsProgress", {
                          count: linesReceived,
                        })
                    : t("devices.liveView.exportPending")}
                </>
              ) : (
                <>
                  <Download size={15} className="text-[#E07A3A]" />
                  {t("devices.liveView.exportRecords")}
                </>
              )}
            </button>
            {/* The device has exactly ONE obvious next action at any moment,
                and it follows from the state: idle -> start, recording -> end. */}
            {primary && (
              <button
                onClick={() => primary.action?.()}
                disabled={controlsLocked || sessionBusy || flowActive}
                className={`flex items-center gap-2 rounded-[10px] px-5 py-2.5 text-sm font-semibold transition duration-150 disabled:opacity-50 ${FOCUS} ${
                  primary.id === "start"
                    ? "text-white shadow-sm disabled:shadow-none"
                    : "border border-[#E5E7EB] bg-white text-[#374151] hover:border-[#FDE68A] hover:bg-[#FFFBEB] hover:text-[#B45309]"
                }`}
                style={
                  primary.id === "start"
                    ? {
                        background:
                          "linear-gradient(135deg, #E8854A 0%, #D66B2E 100%)",
                      }
                    : undefined
                }
              >
                {sessionBusy ? (
                  <Loader2 size={15} className="animate-spin" />
                ) : (
                  <primary.icon size={15} />
                )}
                {primary.label}
              </button>
            )}
          </div>
        </div>

        {/* Impact pulse — one bar per detected impact this connection */}
        {pulse.length > 0 && (
          <div className="flex items-end gap-4 px-6 pb-4">
            <div className="flex h-12 flex-1 items-end gap-[3px]">
              {pulse.map((g, i) => {
                const hot = threshold != null && g >= threshold;
                return (
                  <span
                    key={`${i}-${g}`}
                    title={`${g.toFixed(1)}g`}
                    className="w-[5px] rounded-t-sm"
                    style={{
                      height: `${6 + (g / Math.max(pulseMax, 1)) * 42}px`,
                      backgroundColor: hot ? "#E07A3A" : "#F0C9AC",
                    }}
                  />
                );
              })}
            </div>
            <div className="flex-shrink-0 pb-0.5 text-right">
              <div className="font-mono text-[15px] font-bold tabular-nums text-[#E07A3A]">
                {pulseMax.toFixed(1)}g
              </div>
              <div className="text-[10px] font-medium uppercase tracking-[0.06em] text-[#9CA3AF]">
                {t("devices.liveView.peakImpacts", { count: pulse.length })}
              </div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-2 divide-x divide-[#F3F4F6] border-t border-[#F3F4F6] sm:grid-cols-4">
          {/* Level is driven by `charge` (see utils/wearableStatus): the live
              % is inflated on the charger, so while charging we show the
              cached pre-charge reading, de-emphasised, or no number at all. */}
          <Cell
            label={t("devices.liveView.battery")}
            hint={batt.hint || undefined}
          >
            {batt.state === "unknown" ? (
              "—"
            ) : batt.state === "charging" ? (
              <>
                <span className="text-[#9CA3AF]">
                  {batt.level != null ? `~${batt.level}%` : "—"}
                </span>
                <Suffix className="text-[#15803D]">
                  {t("devices.liveView.charging")}
                </Suffix>
              </>
            ) : batt.state === "complete" ? (
              <>
                100%
                <Suffix className="text-[#15803D]">
                  {t("devices.liveView.full")}
                </Suffix>
              </>
            ) : (
              <span
                className={
                  batt.level != null && batt.level <= 20 ? "text-[#DC2626]" : ""
                }
              >
                {`${batt.level}%`}
              </span>
            )}
          </Cell>
          <Cell
            label={t("devices.liveView.storage")}
            hint={
              devCfg?.bytesUsed != null
                ? t("devices.liveView.bytesUsed", {
                    bytes: devCfg.bytesUsed.toLocaleString(),
                  })
                : undefined
            }
            action={
              <CellAction
                danger
                onClick={onEraseAll}
                disabled={toolsLocked || storeEmpty || inSession}
                title={
                  storeEmpty
                    ? t("devices.summaryCard.nothingToErase")
                    : t("devices.summaryCard.eraseAllTitle")
                }
              >
                {t("common.actions.clear")}
              </CellAction>
            }
          >
            {devCfg?.records != null ? (
              <>
                <span className={devCfg.storeFull ? "text-[#DC2626]" : ""}>
                  {t("devices.liveView.records", { count: devCfg.records })}
                </span>
                <Suffix>
                  {t("devices.liveView.free", {
                    free: devCfg.recordsLeft ?? "?",
                  })}
                </Suffix>
              </>
            ) : (
              "—"
            )}
          </Cell>
          <Cell
            label={t("devices.liveView.threshold")}
            hint={
              devCfg?.eventG != null
                ? t("devices.liveView.thresholdHint", { g: devCfg.eventG })
                : undefined
            }
          >
            {devCfg?.impactG != null ? `${devCfg.impactG} g` : "—"}
          </Cell>
          <Cell
            label={t("devices.liveView.firmware")}
            action={
              <CellAction
                onClick={onUpdateFirmware}
                disabled={toolsLocked}
                title={
                  flowActive
                    ? t("devices.summaryCard.unavailableDuringFlow")
                    : t("devices.summaryCard.updateFirmwareTitle")
                }
              >
                {t("devices.liveView.update")}
              </CellAction>
            }
          >
            <span className="truncate font-mono">{devCfg?.fw || "—"}</span>
          </Cell>
        </div>
      </section>

      {/* ── Session summary — closes the loop when a session ends ── */}
      {sessionSummary && !inSession && (
        <section className="mt-4 flex flex-wrap items-center gap-x-6 gap-y-3 rounded-xl border border-[#D1E7DD] bg-[#F0FDF4] px-5 py-3.5">
          <div className="flex items-center gap-2.5">
            <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-white">
              <Activity size={16} className="text-[#15803D]" />
            </span>
            <div>
              <p className="text-sm font-semibold text-[#14532D]">
                {t("devices.liveView.sessionEnded", {
                  duration: fmtDuration(sessionSummary.durationMs),
                })}
              </p>
              <p className="text-[12px] text-[#15803D]">
                {sessionSummary.peakG != null
                  ? t("devices.liveView.peak", {
                      g: sessionSummary.peakG.toFixed(1),
                    })
                  : t("devices.liveView.noImpacts")}
                {summaryNewRecords != null
                  ? t("devices.liveView.newRecords", {
                      count: summaryNewRecords,
                    })
                  : ""}
              </p>
            </div>
          </div>
          <div className="ml-auto flex items-center gap-2">
            {summaryNewRecords !== 0 && (
              <button
                onClick={onExport}
                disabled={controlsLocked || exportBusy}
                className={`flex items-center gap-1.5 rounded-[10px] bg-[#15803D] px-3.5 py-2 text-[13px] font-semibold text-white transition duration-150 hover:bg-[#166534] disabled:opacity-50 ${FOCUS}`}
              >
                <Download size={13} />
                {t("devices.liveView.exportData")}
              </button>
            )}
            <button
              onClick={onDismissSummary}
              aria-label={t("devices.liveView.dismissSummary")}
              className={`flex h-8 w-8 items-center justify-center rounded-lg text-[#15803D] transition duration-150 hover:bg-white ${FOCUS}`}
            >
              <X size={15} />
            </button>
          </div>
        </section>
      )}
    </div>
  );
};

export default LiveDeviceView;
